import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import "./WordTable.css";
import Autocomplete from "@mui/material/Autocomplete";
import { HashLoader } from "react-spinners";
import {
  OutlinedInput,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  Checkbox,
  Typography,
  Box,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 4;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 260,
    },
  },
};

const WordTable = () => {
  const [mockData, setmockData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    Word: "",
    Pron: "",
    Pos: { operator: "EQUALS", value: [] },
    Etym: "",
    Quizlevel: { operator: "EQUALS", value: [] },
    DateFrom: "",
    DateTo: "",
  });

  const posOptions = [
    "noun",
    "verb",
    "adjective",
    "adverb",
    "preposition",
    "conjunction",
    "interjection",
  ];

  useEffect(() => {
    setLoading(true);
    axios
      .get("https://s3napi.s3nsoftware.com/api/WordOfTheDay/List")
      .then((res) => setmockData(res.data))
      .catch((err) => console.error("Failed to fetch:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      Word: "",
      Pron: "",
      Pos: { operator: "EQUALS", value: [] },
      Etym: "",
      Quizlevel: { operator: "EQUALS", value: [] },
      DateFrom: "",
      DateTo: "",
    });
  };
  const filteredData = useMemo(() => {
    let result = mockData.filter((item) => {
      const wordMatch = (item.Word || "")
        .toLowerCase()
        .includes(filters.Word.toLowerCase());
      const pronMatch = (item.Pron || "")
        .toLowerCase()
        .includes(filters.Pron.toLowerCase());
      const posValue = (item.Pos || "").toString().toLowerCase();
      const selectedPos = filters.Pos.value
        .filter(Boolean)
        .map((v) => v.toString().toLowerCase());

      let posMatch = true;
      if (selectedPos.length) {
        if (filters.Pos.operator === "EQUALS") {
          posMatch = selectedPos.includes(posValue);
        } else if (filters.Pos.operator === "NOT_EQUALS") {
          posMatch = !selectedPos.includes(posValue);
        }
      }

      const etymMatch = (item.Etym || "")
        .toLowerCase()
        .includes(filters.Etym.toLowerCase());
      const selectedQuizLevels = filters.Quizlevel.value
        .filter(Boolean)
        .map((v) => Number(v));

      let quizMatch = true;
      if (selectedQuizLevels.length) {
        if (filters.Quizlevel.operator === "EQUALS") {
          quizMatch = selectedQuizLevels.includes(item.Quizlevel);
        } else if (filters.Quizlevel.operator === "NOT_EQUALS") {
          quizMatch = !selectedQuizLevels.includes(item.Quizlevel);
        }
      }

      const date = new Date(item.Date);
      const from = filters.DateFrom ? new Date(filters.DateFrom) : null;
      const to = filters.DateTo ? new Date(filters.DateTo) : null;
      const dateMatch = (!from || date >= from) && (!to || date <= to);

      return (
        wordMatch &&
        pronMatch &&
        posMatch &&
        etymMatch &&
        quizMatch &&
        dateMatch
      );
    });

    if (sortField) {
      result = [...result].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === "Date") {
          valA = new Date(valA);
          valB = new Date(valB);
        } else {
          valA = (valA || "").toString().toLowerCase();
          valB = (valB || "").toString().toLowerCase();
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [filters, mockData, sortField, sortOrder]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const queryString = useMemo(() => {
    const queryParts = [];
    if (filters.Word) queryParts.push(`Word CONTAINS "${filters.Word}"`);
    if (filters.Pron) queryParts.push(`Pron CONTAINS "${filters.Pron}"`);
    const cleanedPosValues = filters.Pos.value
      .map((v) => v?.toString().trim())
      .filter((v) => v);
    if (cleanedPosValues.length > 0) {
      const operator = filters.Pos.operator === "EQUALS" ? "IN" : "NOT IN";
      queryParts.push(`Pos ${operator} (${cleanedPosValues.join(", ")})`);
    }
    if (filters.Etym) queryParts.push(`Etym CONTAINS "${filters.Etym}"`);
    const cleanedQuizLevels = filters.Quizlevel.value
      .map((v) => v?.toString().trim())
      .filter((v) => v);
    if (cleanedQuizLevels.length > 0) {
      const operator =
        filters.Quizlevel.operator === "EQUALS" ? "IN" : "NOT IN";
      queryParts.push(
        `Quizlevel ${operator} (${cleanedQuizLevels.join(", ")})`
      );
    }
    if (filters.DateFrom) queryParts.push(`Date >= ${filters.DateFrom}`);
    if (filters.DateTo) queryParts.push(`Date <= ${filters.DateTo}`);
    return queryParts.length > 0
      ? queryParts.join(" AND ")
      : "No filters applied";
  }, [filters]);

  const getFilteredWordSuggestions = () => {
    return mockData
      .filter((item) => {
        const pronMatch = (item.Pron || "")
          .toLowerCase()
          .includes(filters.Pron.toLowerCase());

        const posValue = (item.Pos || "").toLowerCase();
        const selectedPos = filters.Pos.value.map((v) => v.toLowerCase());
        let posMatch = true;
        if (selectedPos.length) {
          posMatch =
            filters.Pos.operator === "EQUALS"
              ? selectedPos.includes(posValue)
              : !selectedPos.includes(posValue);
        }

        const etymMatch = (item.Etym || "")
          .toLowerCase()
          .includes(filters.Etym.toLowerCase());

        const selectedQuizLevels = filters.Quizlevel.value.map(Number);
        let quizMatch = true;
        if (selectedQuizLevels.length) {
          quizMatch =
            filters.Quizlevel.operator === "EQUALS"
              ? selectedQuizLevels.includes(item.Quizlevel)
              : !selectedQuizLevels.includes(item.Quizlevel);
        }

        const date = new Date(item.Date);
        const from = filters.DateFrom ? new Date(filters.DateFrom) : null;
        const to = filters.DateTo ? new Date(filters.DateTo) : null;
        const dateMatch = (!from || date >= from) && (!to || date <= to);

        return pronMatch && posMatch && etymMatch && quizMatch && dateMatch;
      })
      .map((item) => item.Word)
      .filter(Boolean)
      .filter((word) =>
        word.toLowerCase().includes(filters.Word.toLowerCase())
      );
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: 100 }}
      >
        <HashLoader color="#7b4462" size={60} />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div className="query-box">
        <strong>Query:</strong> <code>{queryString}</code>
      </div>

      <div className="filters-row">
        <Autocomplete
          freeSolo
          options={
            filters.Word.length > 0
              ? [...new Set(getFilteredWordSuggestions())]
              : []
          }
          inputValue={filters.Word}
          onInputChange={(e, newValue) => {
            setFilters((prev) => ({ ...prev, Word: newValue }));
          }}
          PopperProps={{
            modifiers: [
              {
                name: "zIndex",
                enabled: true,
                phase: "write",
                fn({ state }) {
                  state.styles.popper.zIndex = 1300;
                },
              },
            ],
          }}
          renderInput={(params) => (
            <input
              {...params.inputProps}
              placeholder="Word"
              style={{ height: 36, paddingLeft: 10 }}
            />
          )}
          sx={{ width: 180 }}
        />

        <input
          name="Pron"
          placeholder="Pronunciation"
          value={filters.Pron}
          onChange={handleChange}
        />

        <FormControl sx={{ minWidth: 260 }}>
          <Select
            multiple
            displayEmpty
            value={filters.Pos.value}
            onChange={(e) => {
              const { value } = e.target;
              setFilters((prev) => ({
                ...prev,
                Pos: {
                  ...prev.Pos,
                  value: typeof value === "string" ? value.split(",") : value,
                },
              }));
            }}
            input={<OutlinedInput sx={{ height: 36, fontSize: 13, mt: 1 }} />}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em>Select Parts of Speech</em>
              ) : (
                selected.join(", ")
              )
            }
            MenuProps={MenuProps}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              onClick={(e) => e.stopPropagation()}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize={14} fontWeight={600}>
                  Operator:
                </Typography>
                <Select
                  value={filters.Pos.operator}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      Pos: { ...prev.Pos, operator: e.target.value },
                    }))
                  }
                  variant="standard"
                  disableUnderline
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="EQUALS">Equals</MenuItem>
                  <MenuItem value="NOT_EQUALS">Not Equals</MenuItem>
                </Select>
              </Box>
            </MenuItem>
            <MenuItem divider disabled />
            {posOptions.map((pos) => (
              <MenuItem key={pos} value={pos}>
                <Checkbox checked={filters.Pos.value.includes(pos)} />
                <ListItemText primary={pos} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <Select
            multiple
            displayEmpty
            value={filters.Quizlevel.value}
            onChange={(e) => {
              const { value } = e.target;
              setFilters((prev) => ({
                ...prev,
                Quizlevel: {
                  ...prev.Quizlevel,
                  value: typeof value === "string" ? value.split(",") : value,
                },
              }));
            }}
            input={<OutlinedInput sx={{ height: 36, fontSize: 13, mt: 1 }} />}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em>Select Quiz Levels</em>
              ) : (
                selected.join(", ")
              )
            }
            MenuProps={MenuProps}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              onClick={(e) => e.stopPropagation()}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize={14} fontWeight={600}>
                  Operator:
                </Typography>
                <Select
                  value={filters.Quizlevel.operator}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      Quizlevel: {
                        ...prev.Quizlevel,
                        operator: e.target.value,
                      },
                    }))
                  }
                  variant="standard"
                  disableUnderline
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="EQUALS">Equals</MenuItem>
                  <MenuItem value="NOT_EQUALS">Not Equals</MenuItem>
                </Select>
              </Box>
            </MenuItem>
            <MenuItem divider disabled />
            {["1", "2", "3"].map((level) => (
              <MenuItem key={level} value={level}>
                <Checkbox checked={filters.Quizlevel.value.includes(level)} />
                <ListItemText primary={`Level ${level}`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <input
          name="Etym"
          placeholder="Etymology"
          value={filters.Etym}
          onChange={handleChange}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "14px" }}>From:</span>
          <input
            name="DateFrom"
            type="date"
            value={filters.DateFrom}
            onChange={handleChange}
            style={{ height: "35px", padding: "4px" }}
          />
          <span style={{ fontSize: "14px" }}>To:</span>
          <input
            name="DateTo"
            type="date"
            value={filters.DateTo}
            onChange={handleChange}
            style={{ height: "35px", padding: "4px" }}
          />
        </div>

        <button onClick={handleClearFilters} className="clear-btn">
          Clear
        </button>
      </div>

      <table className="word-table">
        <thead>
          <tr>
            {["Word", "Pron", "Pos", "Defn", "Etym", "Quizlevel", "Date"].map(
              (col) => (
                <th key={col} style={{ cursor: "default", userSelect: "none" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {col}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "4px",
                      }}
                    >
                      <ArrowUpwardIcon
                        style={{
                          fontSize: "14px",
                          cursor: "pointer",
                          color:
                            sortField === col && sortOrder === "asc"
                              ? "#f39c12"
                              : "#bbb",
                          lineHeight: "1",
                        }}
                        onClick={() => {
                          setSortField(col);
                          setSortOrder("asc");
                        }}
                      />
                      <ArrowDownwardIcon
                        style={{
                          fontSize: "14px",
                          cursor: "pointer",
                          color:
                            sortField === col && sortOrder === "desc"
                              ? "#f39c12"
                              : "#bbb",
                          lineHeight: "1",
                        }}
                        onClick={() => {
                          setSortField(col);
                          setSortOrder("desc");
                        }}
                      />
                    </div>
                  </div>
                </th>
              )
            )}
          </tr>
        </thead>

        <tbody>
          {filteredData.length ? (
            paginatedData.map((row, i) => (
              <tr key={i}>
                <td>{row.Word}</td>
                <td>{row.Pron}</td>
                <td>{row.Pos}</td>
                <td>{row.Defn}</td>
                <td>{row.Etym}</td>
                <td>{row.Quizlevel}</td>
                <td>{new Date(row.Date).toLocaleDateString()}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: "center" }}>
                No matching data
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          &laquo; Prev
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index}
            className={currentPage === index + 1 ? "active" : ""}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next &raquo;
        </button>
      </div>
    </div>
  );
};

export default WordTable;
